import { createContext, useContext, type ReactNode } from 'react'

/**
 * Doing one thing to several at once — `/specs/app.spec.md`, Selecting. A selection holds one
 * kind of thing, because what can be done to a set and what can be done to a workout are not
 * the same list, and a bar offering the union of them would offer things half the selection
 * cannot answer. The diary's entries are the third kind, chosen and read the same way.
 */
export type SelectableKind = 'set' | 'workout' | 'entry'

export interface Selection {
  readonly kind: SelectableKind
  /** In the order they were taken in, which is the order the actions work through them. */
  readonly ids: readonly string[]
}

export interface SelectionHandle {
  /** Null while nothing is selected — there is no selection open, not an empty one. */
  readonly selection: Selection | null
  readonly toggle: (kind: SelectableKind, id: string) => void
  readonly clear: () => void
  /**
   * What can be done to the selection, where the page it was made on says so rather than the
   * bar: the diary's entries are made into things the diary knows how to make, and the
   * dialogs that does it with belong to the diary, so the diary lends the bar its controls.
   */
  readonly actions: ReactNode
  readonly setActions: (actions: ReactNode) => void
}

export const SelectionContext = createContext<SelectionHandle | null>(null)

export function useSelection(): SelectionHandle {
  const handle = useContext(SelectionContext)
  if (handle === null) {
    throw new Error('useSelection needs a <SelectionProvider> above it')
  }
  return handle
}

/** How one selectable thing stands against whatever is selected. */
export interface SelectionStance {
  /** Whether a selection of this thing's own kind is open, which is what a press means. */
  readonly selecting: boolean
  readonly selected: boolean
  /**
   * False while a selection of the other kind is open: nothing outside that selection answers
   * a press at all, and nothing outside it offers a way in either.
   */
  readonly answers: boolean
  readonly toggle: () => void
}

export function stanceOf(
  handle: SelectionHandle,
  kind: SelectableKind,
  id: string,
): SelectionStance {
  const { selection, toggle } = handle
  const stance = {
    toggle: () => {
      toggle(kind, id)
    },
  }
  if (selection === null) {
    return { ...stance, selecting: false, selected: false, answers: true }
  }
  if (selection.kind !== kind) {
    return { ...stance, selecting: false, selected: false, answers: false }
  }
  return { ...stance, selecting: true, selected: selection.ids.includes(id), answers: true }
}
